import React from 'react';
import { AdminFormFieldRow } from './AdminFormFieldRow';
import {
  HIRE_HEALTH_AFTEREFFECTS_HINT,
  HIRE_HEALTH_SECTION_AFTEREFFECTS,
  HIRE_HEALTH_SECTION_GENERAL,
  HIRE_HEALTH_SECTION_HEARING,
  HIRE_HEALTH_SECTION_PNEUMO,
  HIRE_HEALTH_SECTION_SPECIALIZED,
  HIRE_HEALTH_SECTION_VIBRATION,
} from './hireHealthSurveyAdminLayout';
import './ProfileSectionPreview.css';

function formatDateJa(raw: string): string {
  const v = raw.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return v || '—';
  const d = new Date(`${v}T12:00:00`);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' });
}

/** {@code true} = 就業可, {@code false} = 就業不可 (matches PDF markers). */
function formatDjJudgement(raw: string): string {
  const v = raw.trim().toLowerCase();
  if (v === 'true') return '可';
  if (v === 'false') return '不可';
  return raw.trim() || '—';
}

function fieldChanged(
  values: Record<string, string>,
  snapshotValues: Record<string, string> | undefined,
  key: string
): boolean {
  if (!snapshotValues || !(key in snapshotValues)) return false;
  return (values[key] ?? '') !== (snapshotValues[key] ?? '');
}

function ExamSectionBlock({
  sectionTitle,
  judgementLabel,
  hcdKey,
  innKey,
  djKey,
  values,
  snapshotValues,
  editing,
  onChange,
}: {
  sectionTitle: string;
  judgementLabel: string;
  hcdKey: string;
  innKey: string;
  djKey: string;
  values: Record<string, string>;
  snapshotValues?: Record<string, string>;
  editing: boolean;
  onChange: (key: string, value: string) => void;
}) {
  const examDate = values[hcdKey] ?? '';
  const hospital = values[innKey] ?? '';
  const judgement = values[djKey] ?? '';

  return (
    <>
      <h3 className="profile-section-preview__section-title">{sectionTitle}</h3>
      <AdminFormFieldRow
        label="受診年月日"
        value={formatDateJa(examDate)}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, hcdKey)}
        compareValue={
          snapshotValues?.[hcdKey] != null ? formatDateJa(snapshotValues[hcdKey]) : undefined
        }
      >
        <input
          type="date"
          className="profile-section-preview__input"
          value={examDate}
          onChange={(e) => onChange(hcdKey, e.target.value)}
        />
      </AdminFormFieldRow>
      <AdminFormFieldRow
        label="受診機関名"
        value={hospital}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, innKey)}
        compareValue={snapshotValues?.[innKey]}
      >
        <input
          className="profile-section-preview__input"
          value={hospital}
          onChange={(e) => onChange(innKey, e.target.value)}
        />
      </AdminFormFieldRow>
      <AdminFormFieldRow
        label={judgementLabel}
        value={formatDjJudgement(judgement)}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, djKey)}
        compareValue={
          snapshotValues?.[djKey] != null ? formatDjJudgement(snapshotValues[djKey]) : undefined
        }
      >
        <select
          className="profile-section-preview__input"
          value={judgement}
          onChange={(e) => onChange(djKey, e.target.value)}
        >
          <option value="">—</option>
          <option value="true">可</option>
          <option value="false">不可</option>
        </select>
      </AdminFormFieldRow>
    </>
  );
}

export function HireHealthSurveyAdminFieldEditor({
  values,
  editing,
  onChange,
  snapshotValues,
}: {
  values: Record<string, string>;
  editing: boolean;
  onChange: (key: string, value: string) => void;
  snapshotValues?: Record<string, string>;
}) {
  const aftereffects = values.aftereffects ?? '';
  const symptoms = values.symptoms ?? '';

  return (
    <div className="profile-section-preview__content">
      <ExamSectionBlock
        sectionTitle={HIRE_HEALTH_SECTION_GENERAL}
        judgementLabel="就業"
        hcdKey="hcdGeneral"
        innKey="inGeneral"
        djKey="djGeneral"
        values={values}
        snapshotValues={snapshotValues}
        editing={editing}
        onChange={onChange}
      />

      <ExamSectionBlock
        sectionTitle={HIRE_HEALTH_SECTION_PNEUMO}
        judgementLabel="粉じん作業"
        hcdKey="hcdPneumoconiosis"
        innKey="inPneumoconiosis"
        djKey="djPneumoconiosis"
        values={values}
        snapshotValues={snapshotValues}
        editing={editing}
        onChange={onChange}
      />

      <ExamSectionBlock
        sectionTitle={HIRE_HEALTH_SECTION_VIBRATION}
        judgementLabel="振動作業"
        hcdKey="hcdVibration"
        innKey="inVibration"
        djKey="djVibration"
        values={values}
        snapshotValues={snapshotValues}
        editing={editing}
        onChange={onChange}
      />

      <ExamSectionBlock
        sectionTitle={HIRE_HEALTH_SECTION_HEARING}
        judgementLabel="坑内作業"
        hcdKey="hcdHearingLoss"
        innKey="inHearingLoss"
        djKey="djHearingLoss"
        values={values}
        snapshotValues={snapshotValues}
        editing={editing}
        onChange={onChange}
      />

      <ExamSectionBlock
        sectionTitle={HIRE_HEALTH_SECTION_SPECIALIZED}
        judgementLabel="溶接作業"
        hcdKey="hcdSpecialized"
        innKey="inSpecialized"
        djKey="djSpecialized"
        values={values}
        snapshotValues={snapshotValues}
        editing={editing}
        onChange={onChange}
      />

      <h3 className="profile-section-preview__section-title">{HIRE_HEALTH_SECTION_AFTEREFFECTS}</h3>
      <p className="profile-section-preview__muted" style={{ margin: '0 0 8px' }}>
        {HIRE_HEALTH_AFTEREFFECTS_HINT}
      </p>

      <AdminFormFieldRow
        label="既往症・後遺症"
        value={aftereffects}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'aftereffects')}
        compareValue={snapshotValues?.aftereffects}
      >
        <textarea
          className="profile-section-preview__input"
          rows={4}
          value={aftereffects}
          onChange={(e) => onChange('aftereffects', e.target.value)}
        />
      </AdminFormFieldRow>

      <AdminFormFieldRow
        label="症状"
        value={symptoms}
        editing={editing}
        changed={fieldChanged(values, snapshotValues, 'symptoms')}
        compareValue={snapshotValues?.symptoms}
      >
        <textarea
          className="profile-section-preview__input"
          rows={4}
          value={symptoms}
          onChange={(e) => onChange('symptoms', e.target.value)}
        />
      </AdminFormFieldRow>
    </div>
  );
}

export default HireHealthSurveyAdminFieldEditor;
